<template>
  <div class="siege-viewport">
    <div class="rotate-controls" aria-label="Board rotation">
      <button
        type="button"
        class="rotate-btn"
        title="Rotate left 90°"
        aria-label="Rotate left 90 degrees"
        @click="rotateBoard(-90)"
      >
        <i class="fas fa-undo" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rotate-btn"
        title="Rotate right 90°"
        aria-label="Rotate right 90 degrees"
        @click="rotateBoard(90)"
      >
        <i class="fas fa-redo" aria-hidden="true"></i>
      </button>
    </div>

    <p class="siege-hint">
      {{ hint }}
    </p>

    <div class="siege-stage">
      <div
        class="siege-iso"
        :class="{ 'is-moving-gate': gameStore.isMovingGate }"
        :style="{ '--board-rotation': `${boardRotation}deg` }"
      >
        <div class="siege-grid">
          <button
            v-for="tile in allTiles"
            :key="`${tile.row}-${tile.col}`"
            type="button"
            class="grid-cell"
            :class="{
              occupied: !!tile.stack,
              selected: isSelectedCell(tile),
              wall: tile.isWall,
              'is-gate': tile.isGate,
              'is-hero': tile.isHero,
            }"
            :style="tile.stack ? { '--unit-color': tile.color } : undefined"
            :aria-label="cellAria(tile)"
            @click="onCellClick(tile.row, tile.col)"
            @contextmenu.prevent="onCellRightClick(tile.row, tile.col)"
          >
            <span v-if="tile.isGate" class="cell-anchor">
              <span class="cell-billboard">
                <span class="cell-content gate-sprite">
                  <i class="fas fa-dungeon" aria-hidden="true"></i>
                  <span class="cell-qty">Gate</span>
                </span>
              </span>
            </span>
            <span v-else-if="tile.isHero" class="cell-anchor">
              <span class="cell-billboard">
                <span class="cell-content unit-sprite hero-sprite">
                  <img
                    v-if="tile.heroImage"
                    class="hero-portrait"
                    :src="tile.heroImage"
                    alt=""
                  />
                  <i v-else class="fas fa-crown" aria-hidden="true"></i>
                  <span class="cell-qty">Hero</span>
                </span>
              </span>
            </span>
            <span v-else-if="tile.stack" class="cell-anchor">
              <span class="cell-billboard">
                <span class="cell-content unit-sprite">
                  <i :class="tile.icon" aria-hidden="true"></i>
                  <span class="cell-qty">{{ tile.stack.quantity }}</span>
                </span>
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from "vue";
import { useGameStore } from "../store/game";
import { GATE_ROW, GRID_SIZE, gridCells } from "../utils/defenseFormation";
import { unitDisplayMap } from "../utils/unitDisplay";

const props = defineProps({
  cells: {
    type: Array,
    default: () => [],
  },
  selectedSlug: {
    type: String,
    default: null,
  },
  placeQty: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(["cell-click"]);

const gameStore = useGameStore();
const boardRotation = ref(0);

const cellMap = computed(() => {
  const map = {};
  for (const cell of props.cells || []) {
    map[`${cell.row},${cell.col}`] = cell;
  }
  return map;
});

const gateCol = computed(() =>
  Math.max(0, Math.min(GRID_SIZE - 1, Number(gameStore.defenseDraftGateCol) || 0))
);

const garrisonHero = computed(() => gameStore.defenseGarrisonHero);

const heroVisual = computed(() => {
  const placed = garrisonHero.value;
  if (!placed) return null;
  const hero = gameStore.playerHeroes.find(
    (entry) => Number(entry.id) === Number(placed.heroId)
  );
  const card = gameStore.socketedHeroCard(placed.heroId);
  return {
    ...placed,
    name: hero?.name || "Hero",
    image: card?.image || "",
  };
});

const allTiles = computed(() =>
  gridCells().map(({ row, col }) => {
    const isWall = row === GATE_ROW;
    const isGate = isWall && col === gateCol.value;
    const isHero =
      !isGate &&
      garrisonHero.value &&
      garrisonHero.value.row === row &&
      garrisonHero.value.col === col;
    const stack = isGate
      ? null
      : isHero
      ? { unit_slug: "hero", quantity: 1, isHero: true }
      : cellMap.value[`${row},${col}`] || null;
    const display = stack && !isHero ? unitDisplayMap[stack.unit_slug] : null;
    return {
      row,
      col,
      isWall,
      isGate,
      isHero,
      stack,
      heroImage: isHero ? heroVisual.value?.image || "" : "",
      icon: isHero ? "fas fa-crown" : display?.icon || "fas fa-users",
      color: isHero ? "#f0d060" : display?.color || "#d8d0c0",
    };
  })
);

const hint = computed(() => {
  const gateHint = gameStore.isMovingGate
    ? "City Gate picked up. Click a wall tile to drop it, or click elsewhere to cancel."
    : "Click the City Gate to pick it up, then click a wall tile to drop it. Right-click occupied tiles to remove the slider quantity.";
  if (gameStore.isMovingGate) return gateHint;
  if (gameStore.defenseSelectedHeroId) {
    return `Click a tile to deploy the hero. ${gateHint}`;
  }
  if (!props.selectedSlug) {
    return `Select a unit in the panel, then click a tile. Click a filled tile with no unit selected to clear it. ${gateHint}`;
  }
  const qty = Math.max(0, Math.floor(Number(props.placeQty) || 0));
  const name =
    unitDisplayMap[props.selectedSlug]?.name || props.selectedSlug;
  if (qty < 1) return `No remaining ${name} to place. ${gateHint}`;
  return `Click a tile to place ${qty} ${name}. ${gateHint}`;
});

const isSelectedCell = (tile) =>
  !!(
    tile.stack &&
    ((gameStore.defenseSelectedHeroId && tile.isHero) ||
      (props.selectedSlug && tile.stack.unit_slug === props.selectedSlug))
  );

const cellAria = (tile) => {
  if (tile.isGate) return `City Gate, column ${tile.col + 1}`;
  if (tile.isHero) {
    return `${heroVisual.value?.name || "Hero"}, row ${tile.row + 1} column ${tile.col + 1}`;
  }
  if (tile.stack) {
    const name = unitDisplayMap[tile.stack.unit_slug]?.name || tile.stack.unit_slug;
    return `${name} x${tile.stack.quantity}, row ${tile.row + 1} column ${tile.col + 1}`;
  }
  if (tile.isWall) return `City wall, column ${tile.col + 1}`;
  return `Empty tile row ${tile.row + 1} column ${tile.col + 1}`;
};

const rotateBoard = (delta) => {
  boardRotation.value += delta;
};

const onDocumentClick = (event) => {
  if (!gameStore.isMovingGate) return;
  if (event.target?.closest?.(".grid-cell")) return;
  gameStore.isMovingGate = false;
};

watch(
  () => gameStore.isMovingGate,
  (moving) => {
    if (moving) {
      document.addEventListener("click", onDocumentClick, true);
    } else {
      document.removeEventListener("click", onDocumentClick, true);
    }
  }
);

onUnmounted(() => {
  document.removeEventListener("click", onDocumentClick, true);
  gameStore.isMovingGate = false;
});

const onCellClick = (row, col) => {
  if (gameStore.isMovingGate) {
    if (row === GATE_ROW) {
      gameStore.moveDefenseGate(col);
    }
    gameStore.isMovingGate = false;
    return;
  }
  if (row === GATE_ROW && col === gateCol.value) {
    gameStore.isMovingGate = true;
    return;
  }
  emit("cell-click", { row, col });
};

const onCellRightClick = (row, col) => {
  if (row === GATE_ROW && col === gateCol.value) return;
  const qty = Math.max(1, Number(gameStore.defensePlaceQty) || 1);
  gameStore.removeDefenseUnits(row, col, qty);
};
</script>

<style scoped>
.siege-viewport {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  perspective: 1100px;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 40% 55%, rgba(20, 28, 48, 0.35), rgba(8, 10, 18, 0.72));
}

.rotate-controls {
  position: absolute;
  left: 0;
  right: 380px;
  bottom: 18px;
  z-index: 4;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
}

.rotate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  margin: 0;
  border: 1px solid rgba(0, 246, 255, 0.35);
  border-radius: 10px;
  background: rgba(8, 12, 24, 0.78);
  color: #00f6ff;
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 0 12px rgba(0, 240, 255, 0.18);
}

.rotate-btn i {
  display: block;
  line-height: 1;
  width: 1em;
  text-align: center;
}

.rotate-btn:hover {
  background: rgba(0, 40, 50, 0.9);
}

.siege-hint {
  position: absolute;
  top: 18px;
  right: 390px;
  left: auto;
  bottom: auto;
  z-index: 4;
  pointer-events: none;
  max-width: min(440px, calc(100% - 430px));
  margin: 0;
  color: #c9d2e8;
  font-family: "Exo 2", sans-serif;
  font-size: 0.85rem;
  text-align: right;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.85);
}

.siege-stage {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 380px 6.5rem 3rem;
  box-sizing: border-box;
  pointer-events: none;
  transform-style: preserve-3d;
}

.siege-iso {
  --board-rotation: 0deg;
  transform: rotateX(60deg) rotateZ(var(--board-rotation, 0deg));
  transform-style: preserve-3d;
  pointer-events: none;
  transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.siege-grid {
  --cell: clamp(38px, 5.2vh, 52px);
  width: calc(var(--cell) * 10);
  height: calc(var(--cell) * 10);
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  grid-template-rows: repeat(10, 1fr);
  gap: 3px;
  padding: 6px;
  box-sizing: border-box;
  pointer-events: none;
  background:
    linear-gradient(145deg, rgba(42, 38, 32, 0.95), rgba(18, 16, 14, 0.98));
  border: 1px solid rgba(201, 180, 138, 0.28);
  box-shadow:
    inset 0 0 24px rgba(0, 0, 0, 0.55),
    inset 0 2px 0 rgba(255, 230, 180, 0.08),
    18px 28px 24px rgba(0, 0, 0, 0.55);
  transform-style: preserve-3d;
}

.grid-cell {
  position: relative;
  overflow: visible;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  pointer-events: auto;
  background:
    linear-gradient(145deg, rgba(54, 62, 82, 0.9), rgba(22, 26, 38, 0.96));
  box-shadow:
    inset 2px 2px 0 rgba(255, 255, 255, 0.08),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(80, 90, 120, 0.35);
  transform-style: preserve-3d;
}

.grid-cell::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: auto;
}

.grid-cell:hover {
  background:
    linear-gradient(145deg, rgba(70, 90, 120, 0.95), rgba(30, 40, 58, 0.96));
  box-shadow:
    inset 2px 2px 0 rgba(0, 246, 255, 0.18),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 10px rgba(0, 246, 255, 0.25);
}

.grid-cell.wall {
  background:
    linear-gradient(180deg, #6b5a42 0%, #3d3328 42%, #241c16 100%);
  box-shadow:
    inset 0 3px 0 rgba(255, 220, 160, 0.22),
    inset 0 -8px 12px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(120, 96, 58, 0.65);
}

.grid-cell.wall:hover {
  box-shadow:
    inset 0 3px 0 rgba(255, 220, 160, 0.3),
    0 0 10px rgba(201, 180, 138, 0.35);
}

.grid-cell.is-gate {
  box-shadow:
    inset 0 3px 0 rgba(255, 220, 160, 0.3),
    0 0 0 1px #c9b48a,
    0 0 12px rgba(201, 180, 138, 0.45);
}

.grid-cell.occupied {
  background:
    linear-gradient(145deg, rgba(40, 48, 40, 0.95), rgba(16, 20, 18, 0.96));
  box-shadow:
    inset 2px 2px 0 rgba(255, 255, 255, 0.1),
    inset -3px -4px 8px rgba(0, 0, 0, 0.6),
    0 0 0 1px var(--unit-color),
    0 0 10px rgba(125, 255, 163, 0.28);
}

.grid-cell.selected {
  box-shadow:
    inset 2px 2px 0 rgba(0, 246, 255, 0.25),
    0 0 0 1px #00f6ff,
    0 0 12px rgba(0, 246, 255, 0.4);
}

.cell-anchor {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  width: 0;
  height: 0;
  pointer-events: none;
  transform-style: preserve-3d;
}

.cell-billboard {
  position: absolute;
  left: 0;
  top: 0;
  transform: rotateZ(calc(var(--board-rotation, 0deg) * -1)) rotateX(-60deg);
  transform-origin: 0 0;
  transform-style: preserve-3d;
  pointer-events: none;
}

.cell-content {
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transform: translateX(-50%);
  color: var(--unit-color, #f4f7ff);
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
  pointer-events: none;
  white-space: nowrap;
}

.cell-content i {
  font-size: 0.95rem;
}

.gate-sprite {
  color: #f4e6c4;
}

.unit-sprite {
  animation: hoverFloat 3s ease-in-out infinite;
}

.hero-sprite {
  color: #f0d060;
  filter: drop-shadow(0 0 8px rgba(240, 208, 96, 0.75));
}

.hero-portrait {
  width: 22px;
  height: 22px;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid #f0d060;
  box-shadow: 0 0 8px rgba(240, 208, 96, 0.55);
}

.grid-cell.occupied.is-hero {
  box-shadow:
    inset 2px 2px 0 rgba(240, 208, 96, 0.28),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 0 1px #f0d060,
    0 0 10px rgba(240, 208, 96, 0.35);
}

.grid-cell.occupied.is-hero.selected {
  box-shadow:
    inset 2px 2px 0 rgba(240, 208, 96, 0.35),
    0 0 0 1px #f0d060,
    0 0 12px rgba(240, 208, 96, 0.55);
}

.cell-qty {
  font-family: "Orbitron", sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  color: #f4f7ff;
}

@keyframes hoverFloat {
  0%,
  100% {
    transform: translateX(-50%) translateY(0px);
  }
  50% {
    transform: translateX(-50%) translateY(-8px);
  }
}

.siege-iso.is-moving-gate .grid-cell {
  cursor: crosshair;
}

.siege-iso.is-moving-gate .grid-cell.wall {
  animation: wallDropPulse 1.2s ease-in-out infinite;
}

.siege-iso.is-moving-gate .gate-sprite {
  opacity: 0.45;
  animation: gatePickupPulse 1s ease-in-out infinite;
}

@keyframes wallDropPulse {
  0%,
  100% {
    box-shadow:
      inset 0 3px 0 rgba(255, 220, 160, 0.22),
      inset 0 -8px 12px rgba(0, 0, 0, 0.55),
      0 0 0 1px rgba(201, 180, 138, 0.65),
      0 0 8px rgba(201, 180, 138, 0.25);
  }
  50% {
    box-shadow:
      inset 0 3px 0 rgba(255, 220, 160, 0.4),
      inset 0 -8px 12px rgba(0, 0, 0, 0.55),
      0 0 0 2px #f4e6c4,
      0 0 16px rgba(244, 230, 196, 0.55);
  }
}

@keyframes gatePickupPulse {
  0%,
  100% {
    opacity: 0.35;
    transform: translateX(-50%) translateY(0px);
  }
  50% {
    opacity: 0.7;
    transform: translateX(-50%) translateY(-6px);
  }
}
</style>
